import React from 'react'
import { useDragLayer, XYCoord } from 'react-dnd'
import { ItemType } from '../../Documents/item-type'
import { Icon, IconChoices } from '../../../common/Icon'
import { theme } from '../../../../theme/theme'
import { cn } from '../../../../helpers/utils'

const getItemStyles = (currentOffset: XYCoord | null): React.CSSProperties => {
  if (!currentOffset) {
    return {
      display: 'none',
    }
  }

  const { x, y } = currentOffset
  const transform = `translate(${x}px, ${y}px)`

  return {
    transform,
    WebkitTransform: transform,
  }
}

interface DragItem {
  _id: string
  name?: string
  filename?: string
  type?: string
  thumbnail?: string
  isListMode?: boolean
}

export const CustomDragLayer = (): JSX.Element | null => {
  const { itemType, isDragging, item, currentOffset } = useDragLayer(
    (monitor) => ({
      item: monitor.getItem() as DragItem | null,
      itemType: monitor.getItemType(),
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
    })
  )

  if (!isDragging || !item) {
    return null
  }

  const isFolder = itemType === ItemType.FOLDER
  const isListMode = item.isListMode ?? true

  // Card view preview
  if (!isListMode) {
    return (
      <div className="fixed pointer-events-none z-50 left-0 top-0 w-full h-full">
        <div style={getItemStyles(currentOffset)}>
          <div
            className="flex h-[200px] w-[200px] flex-col overflow-hidden rounded-lg border border-neutral-80 bg-white shadow-lg"
            style={{ opacity: 0.3 }}
          >
            <div
              className={cn(
                'flex h-full w-full items-center justify-center rounded-t-lg',
                !item.thumbnail && 'bg-neutral-94'
              )}
              style={
                item.thumbnail
                  ? {
                      backgroundImage: `url(${item.thumbnail})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : undefined
              }
            >
              {!item.thumbnail && (
                <Icon
                  size={64}
                  icon={isFolder ? IconChoices.FOLDER : IconChoices.DOCUMENT}
                />
              )}
            </div>
            <div className="flex w-full flex-col p-3">
              <p
                className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                style={theme.textVariants.base}
              >
                {item.name || item.filename || ''}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // List view preview
  return (
    <div className="fixed pointer-events-none z-50 left-0 top-0 w-full h-full">
      <div style={getItemStyles(currentOffset)}>
        <div
          className="flex items-center gap-3 rounded-lg border border-neutral-80 bg-white px-4 py-3 shadow-lg"
          style={{ opacity: 0.3 }}
        >
          {item.thumbnail ? (
            <div
              className="h-8 w-8 rounded-md bg-neutral-94 bg-cover bg-center"
              style={{
                backgroundImage: `url(${item.thumbnail})`,
              }}
            />
          ) : (
            <Icon
              size={32}
              icon={isFolder ? IconChoices.FOLDER : IconChoices.DOCUMENT}
            />
          )}
          <p
            style={theme.textVariants.base}
            className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {item.name || item.filename || ''}
          </p>
        </div>
      </div>
    </div>
  )
}
